import { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { RefreshCw, TrendingUp, TrendingDown, DollarSign, PieChart } from "lucide-react";
import { useStablecoinsData, StablecoinItem, StablecoinHistoryPoint } from "@/hooks/useStablecoinsData";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, Brush, Legend } from "recharts";
import { format } from "date-fns";
import { SSRAlphaCard } from "@/components/SSRAlphaCard";

const STABLECOIN_COLORS = {
  USDT: '#26A17B',
  USDC: '#2775CA',
  Others: '#6B7280',
};

function MiniDonutChart({ percentage, color }: { percentage: number; color: string }) {
  const radius = 20;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  return (
    <svg width="56" height="56" viewBox="0 0 56 56">
      <circle
        cx="28"
        cy="28"
        r={radius}
        fill="none"
        stroke="hsl(var(--muted))"
        strokeWidth="6"
      />
      <circle
        cx="28"
        cy="28"
        r={radius}
        fill="none"
        stroke={color}
        strokeWidth="6"
        strokeLinecap="round"
        strokeDasharray={circumference}
        strokeDashoffset={strokeDashoffset}
        transform="rotate(-90 28 28)"
      />
      <text
        x="28"
        y="28"
        textAnchor="middle"
        dominantBaseline="middle"
        className="fill-foreground text-xs font-semibold"
      >
        {percentage.toFixed(0)}%
      </text>
    </svg>
  );
}

function formatTooltipValue(value: number): string {
  if (value >= 1e12) return `$${(value / 1e12).toFixed(2)}T`;
  if (value >= 1e9) return `$${(value / 1e9).toFixed(2)}B`;
  if (value >= 1e6) return `$${(value / 1e6).toFixed(2)}M`;
  return `$${value.toFixed(0)}`;
}

function SummaryCards({ data, isLoading }: { data: any; isLoading: boolean }) {
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[...Array(4)].map((_, i) => (
          <Card key={i} className="bg-card/50 backdrop-blur">
            <CardContent className="p-4">
              <Skeleton className="h-4 w-20 mb-2" />
              <Skeleton className="h-8 w-28" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  const cards = [
    {
      title: "USDT Supply",
      value: data?.current?.usdtFormatted || "—",
      color: STABLECOIN_COLORS.USDT,
      icon: DollarSign,
    },
    {
      title: "USDC Supply",
      value: data?.current?.usdcFormatted || "—",
      color: STABLECOIN_COLORS.USDC,
      icon: DollarSign,
    },
    {
      title: "Total Stablecoins",
      value: data?.current?.totalFormatted || "—",
      color: "hsl(var(--primary))",
      icon: PieChart,
    },
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {cards.map((card, i) => (
        <Card key={i} className="bg-card/50 backdrop-blur border-border/50">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground mb-1">{card.title}</p>
                <p className="text-2xl font-bold" style={{ color: card.color }}>
                  {card.value}
                </p>
              </div>
              <card.icon className="h-5 w-5 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
      ))}
      
      {/* Dominance Card with Donut */}
      <Card className="bg-card/50 backdrop-blur border-border/50">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-muted-foreground mb-1">USDT Dominance</p>
              <p className="text-lg font-semibold" style={{ color: STABLECOIN_COLORS.USDT }}>
                {data?.current?.usdtDominance?.toFixed(1) || "—"}%
              </p>
            </div>
            <MiniDonutChart 
              percentage={data?.current?.usdtDominance || 0} 
              color={STABLECOIN_COLORS.USDT} 
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// Tooltip component for charts
function ChartTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  
  const formattedDate = label ? format(new Date(label), 'MMM dd, yyyy') : '';
  
  return (
    <div className="bg-popover/95 backdrop-blur border border-border rounded-lg px-3 py-2 shadow-lg">
      <p className="text-xs text-muted-foreground mb-1">{formattedDate}</p>
      {payload.map((entry: any, index: number) => (
        <p key={index} className="text-sm font-medium" style={{ color: entry.color }}>
          {entry.name}: {formatTooltipValue(entry.value)}
        </p>
      ))}
    </div>
  );
}

type ViewMode = 'total' | 'breakdown';

function SupplyChart({ data, isLoading }: { data: any; isLoading: boolean }) {
  const chartData: StablecoinHistoryPoint[] = data?.history || [];
  const [viewMode, setViewMode] = useState<ViewMode>('total');
  
  // Calculate dynamic Y-axis domain based on visible data
  const yDomain = useMemo(() => {
    if (!chartData.length) return [0, 200e9];
    
    const values = chartData.map((d) => d.total).filter((v) => v > 0);
    if (!values.length) return [0, 200e9];
    
    const min = Math.min(...values);
    const max = Math.max(...values);
    const padding = (max - min) * 0.05; // 5% padding
    
    return [Math.max(0, min - padding), max + padding];
  }, [chartData]);

  if (isLoading) {
    return (
      <Card className="bg-card/50 backdrop-blur">
        <CardHeader className="pb-2">
          <CardTitle className="text-lg">Stablecoin Supply</CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[400px] w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-card/50 backdrop-blur border-border/50">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg">Stablecoin Supply</CardTitle>
          <Tabs value={viewMode} onValueChange={(v) => setViewMode(v as ViewMode)}>
            <TabsList className="h-8 bg-muted/50">
              <TabsTrigger value="total" className="text-xs px-3">
                Total
              </TabsTrigger>
              <TabsTrigger value="breakdown" className="text-xs px-3">
                USDT/USDC
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={400}>
          <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 40 }}>
            <defs>
              <linearGradient id="totalGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.4} />
                <stop offset="95%" stopColor="#3B82F6" stopOpacity={0.05} />
              </linearGradient>
              <linearGradient id="usdtGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={STABLECOIN_COLORS.USDT} stopOpacity={0.6} />
                <stop offset="95%" stopColor={STABLECOIN_COLORS.USDT} stopOpacity={0.1} />
              </linearGradient>
              <linearGradient id="usdcGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={STABLECOIN_COLORS.USDC} stopOpacity={0.6} />
                <stop offset="95%" stopColor={STABLECOIN_COLORS.USDC} stopOpacity={0.1} />
              </linearGradient>
              <linearGradient id="othersGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={STABLECOIN_COLORS.Others} stopOpacity={0.4} />
                <stop offset="95%" stopColor={STABLECOIN_COLORS.Others} stopOpacity={0.1} />
              </linearGradient>
              {/* Brush background gradient */}
              <linearGradient id="brushGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#3B82F6" stopOpacity={0.3} />
                <stop offset="100%" stopColor="#3B82F6" stopOpacity={0.1} />
              </linearGradient>
            </defs>
            <XAxis 
              dataKey="date"
              tickFormatter={(ts) => format(new Date(ts), 'MMM yy')}
              tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis 
              domain={viewMode === 'total' ? yDomain : ['auto', 'auto']}
              tickFormatter={(v) => `$${(v / 1e9).toFixed(0)}B`}
              tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }}
              axisLine={false}
              tickLine={false}
              width={55}
            />
            <Tooltip content={<ChartTooltip />} />
            
            {viewMode === 'total' ? (
              <Area
                type="monotone"
                dataKey="total"
                name="Total"
                stroke="#3B82F6"
                strokeWidth={2}
                fill="url(#totalGradient)"
              />
            ) : (
              <>
                <Area
                  type="monotone"
                  dataKey="others"
                  name="Others"
                  stackId="1"
                  stroke={STABLECOIN_COLORS.Others}
                  strokeWidth={0}
                  fill="url(#othersGradient)"
                />
                <Area
                  type="monotone"
                  dataKey="usdc"
                  name="USDC"
                  stackId="1"
                  stroke={STABLECOIN_COLORS.USDC}
                  strokeWidth={1}
                  fill="url(#usdcGradient)"
                />
                <Area
                  type="monotone"
                  dataKey="usdt"
                  name="USDT"
                  stackId="1"
                  stroke={STABLECOIN_COLORS.USDT}
                  strokeWidth={1}
                  fill="url(#usdtGradient)"
                />
                <Legend 
                  verticalAlign="top"
                  align="right"
                  wrapperStyle={{ paddingBottom: 10 }}
                  formatter={(value) => <span className="text-xs text-muted-foreground">{value}</span>}
                />
              </>
            )}
            
            {/* Brush for period selection - shows full history by default */}
            <Brush
              dataKey="date"
              height={35}
              stroke="#3B82F6"
              fill="hsl(215 20% 16%)"
              tickFormatter={(ts) => format(new Date(ts), 'MMM yy')}
              startIndex={0}
              endIndex={chartData.length - 1}
              travellerWidth={10}
            >
              <AreaChart data={chartData}>
                <Area
                  type="monotone"
                  dataKey="total"
                  stroke="#3B82F6"
                  strokeWidth={1}
                  fill="url(#brushGradient)"
                />
              </AreaChart>
            </Brush>
          </AreaChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}

function DominanceChart({ data, isLoading }: { data: any; isLoading: boolean }) {
  if (isLoading) {
    return (
      <Card className="bg-card/50 backdrop-blur">
        <CardHeader>
          <CardTitle className="text-lg">Stablecoin Dominance</CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[300px] w-full" />
        </CardContent>
      </Card>
    );
  }

  // Build static dominance data for display
  const current = data?.current;
  const dominanceData = [
    { name: 'USDT', value: current?.usdtDominance || 0, color: STABLECOIN_COLORS.USDT },
    { name: 'USDC', value: current?.usdcDominance || 0, color: STABLECOIN_COLORS.USDC },
    { name: 'Others', value: 100 - (current?.usdtDominance || 0) - (current?.usdcDominance || 0), color: STABLECOIN_COLORS.Others },
  ];

  return (
    <Card className="bg-card/50 backdrop-blur border-border/50">
      <CardHeader>
        <CardTitle className="text-lg">Stablecoin Dominance</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {dominanceData.map((item) => (
            <div key={item.name} className="flex items-center gap-3">
              <div 
                className="w-3 h-3 rounded-full flex-shrink-0" 
                style={{ backgroundColor: item.color }}
              />
              <span className="text-sm font-medium w-16">{item.name}</span>
              <div className="flex-1 h-3 bg-muted rounded-full overflow-hidden">
                <div 
                  className="h-full rounded-full transition-all duration-500"
                  style={{ 
                    width: `${item.value}%`, 
                    backgroundColor: item.color,
                  }}
                />
              </div>
              <span className="text-sm font-mono w-16 text-right">
                {item.value.toFixed(1)}%
              </span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function StablecoinsTable({ stablecoins, isLoading }: { stablecoins: StablecoinItem[]; isLoading: boolean }) {
  if (isLoading) {
    return (
      <Card className="bg-card/50 backdrop-blur">
        <CardHeader>
          <CardTitle className="text-lg">Top Stablecoins</CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[400px] w-full" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="bg-card/50 backdrop-blur border-border/50">
      <CardHeader>
        <CardTitle className="text-lg">Top Stablecoins</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow className="border-border/50">
              <TableHead className="pl-6">Stablecoin</TableHead>
              <TableHead className="text-right">Supply</TableHead>
              <TableHead className="text-right">7d Change</TableHead>
              <TableHead className="text-right pr-6">Dominance</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {stablecoins?.map((coin) => (
              <TableRow key={coin.id} className="border-border/50 hover:bg-muted/50">
                <TableCell className="pl-6">
                  <div className="flex items-center gap-2">
                    <div 
                      className="w-2.5 h-2.5 rounded-full" 
                      style={{ backgroundColor: coin.color }}
                    />
                    <span className="font-medium">{coin.symbol}</span>
                    <span className="text-muted-foreground text-sm hidden sm:inline">
                      {coin.name}
                    </span>
                  </div>
                </TableCell>
                <TableCell className="text-right font-mono">
                  {coin.supplyFormatted}
                </TableCell>
                <TableCell className="text-right">
                  <span className={`flex items-center justify-end gap-1 ${
                    coin.change7d >= 0 ? 'text-emerald-500' : 'text-red-500'
                  }`}>
                    {coin.change7d >= 0 ? (
                      <TrendingUp className="h-3 w-3" />
                    ) : (
                      <TrendingDown className="h-3 w-3" />
                    )}
                    {coin.change7d >= 0 ? '+' : ''}{coin.change7d.toFixed(2)}%
                  </span>
                </TableCell>
                <TableCell className="text-right pr-6 font-mono">
                  {coin.dominance.toFixed(2)}%
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

export default function Stablecoins() {
  const { data, isLoading, refetch, isFetching } = useStablecoinsData();

  return (
    <div className="container mx-auto p-4 md:p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Stablecoins Dashboard</h1>
          <p className="text-muted-foreground text-sm">
            Supply tracking and dominance analysis
          </p>
        </div>
        <Button 
          variant="outline" 
          size="icon" 
          onClick={() => refetch()}
          disabled={isFetching}
        >
          <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      {/* Summary Cards */}
      <SummaryCards data={data} isLoading={isLoading} />

      {/* Supply Chart - Full Width */}
      <SupplyChart data={data} isLoading={isLoading} />

      {/* SSR Alpha Signal */}
      <SSRAlphaCard />

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <DominanceChart data={data} isLoading={isLoading} />
        <StablecoinsTable stablecoins={data?.stablecoins || []} isLoading={isLoading} />
      </div>
    </div>
  );
}
